import { useState, useCallback, useEffect, Suspense, lazy } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import SearchOverlay from "@/components/ui/SearchOverlay";
import BackToTop from "@/components/ui/BackToTop";
import ErrorBoundary from "@/components/ui/ErrorBoundary";
import { CALLBACK_PATH, useAuth } from "@/lib/auth";
import { SCHOLAR_ENABLED } from "@/lib/features";

/* ============================================
   Lazy-loaded Routes — Code-split per page
   ============================================ */

const LandingPage = lazy(() => import("@/pages/LandingPage"));
const SeerahPage = lazy(() => import("@/pages/SeerahPage"));
const SeerahDetailPage = lazy(() => import("@/pages/SeerahDetailPage"));
const TimelinePage = lazy(() => import("@/pages/TimelinePage"));
const TimelineDetailPage = lazy(() => import("@/pages/TimelineDetailPage"));
const QuranPage = lazy(() => import("@/pages/QuranPage"));
const QuranDetailPage = lazy(() => import("@/pages/QuranDetailPage"));
const ClarifierPage = lazy(() => import("@/pages/ClarifierPage"));
const IsnadPage = lazy(() => import("@/pages/IsnadPage"));
const LearningPage = lazy(() => import("@/pages/LearningPage"));
const ScholarPage = SCHOLAR_ENABLED
  ? lazy(() => import("@/pages/ScholarPage"))
  : lazy(() => import("@/pages/ScholarComingSoonPage"));
const DhikrPage = lazy(() => import("@/pages/DhikrPage"));
const AnalyticsPage = lazy(() => import("@/pages/AnalyticsPage"));
const BookmarksPage = lazy(() => import("@/pages/BookmarksPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

/* ============================================
   Loading Spinner for Suspense
   ============================================ */

function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center">
        <div className="w-10 h-10 border-3 border-[var(--color-gold)] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-sm text-[var(--color-ink-muted)]">Loading...</p>
      </div>
    </div>
  );
}

/** Landing spot for the Cognito redirect; the auth provider finishes sign-in and navigates on. */
function AuthCallback() {
  const auth = useAuth();
  if (auth.error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4 text-center">
        <div>
          <p className="text-[var(--color-ink-muted)] mb-4">Sign-in didn't complete. Please try again.</p>
          <button onClick={auth.signIn} className="btn-gold">Sign in</button>
        </div>
      </div>
    );
  }
  return <PageLoader />;
}

/* ============================================
   App
   ============================================ */

export default function App() {
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);

  const openSearch = useCallback(() => setSearchOpen(true), []);
  const closeSearch = useCallback(() => setSearchOpen(false), []);

  // Cmd+K / Ctrl+K global shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Scroll to top on navigation, or to the #anchor once its content has rendered.
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo({ top: 0 });
      return;
    }
    const id = decodeURIComponent(location.hash.slice(1));
    let tries = 0;
    const timer = window.setInterval(() => {
      const el = document.getElementById(id);
      if (el || ++tries > 40) {
        window.clearInterval(timer);
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
    return () => window.clearInterval(timer);
  }, [location.pathname, location.hash]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar onSearchClick={openSearch} />
      <SearchOverlay isOpen={searchOpen} onClose={closeSearch} />
      <BackToTop />

      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[200] focus:px-4 focus:py-2 focus:rounded-lg focus:bg-white focus:text-[var(--color-midnight)]">
        Skip to content
      </a>
      <ErrorBoundary resetKey={location.pathname}>
        <div className="flex-1" id="main-content">
          <Suspense fallback={<PageLoader />}>
            <AnimatePresence mode="wait">
              <Routes location={location} key={location.pathname}>
                <Route path="/" element={<LandingPage />} />
                <Route path="/seerah" element={<SeerahPage />} />
                <Route path="/seerah/:id" element={<SeerahDetailPage />} />
                <Route path="/timeline" element={<TimelinePage />} />
                <Route path="/timeline/:id" element={<TimelineDetailPage />} />
                <Route path="/quran" element={<QuranPage />} />
                <Route path="/quran/:surahNum" element={<QuranDetailPage />} />
                <Route path="/clarifier" element={<ClarifierPage />} />
                <Route path="/isnad" element={<IsnadPage />} />
                <Route path="/learning" element={<LearningPage />} />
                <Route path="/scholar" element={<ScholarPage />} />
                <Route path="/dhikr" element={<DhikrPage />} />
                <Route path="/analytics" element={<AnalyticsPage />} />
                <Route path="/bookmarks" element={<BookmarksPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path={CALLBACK_PATH} element={<AuthCallback />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </AnimatePresence>
          </Suspense>
        </div>
      </ErrorBoundary>

      <Footer />
    </div>
  );
}
